import { type ViolationDetail } from '@aysar/contracts';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useToast } from '@/shared/hooks/useToast/useToast';
import { type ModalData } from '@/shared/store/reducers/modal/modal.types';
import { toApiError } from '@/shared/utils/transform-error/transform-error';
import { type ApiError } from '@/shared/utils/transform-error/transform-error.types';

import {
  useEnforceViolationMutation,
  useViolationQuery,
} from '../../services/violations/violations.api';
import { violationErrorCopy } from '../../utils/violation-error/violation-error.util';

export interface UseEnforceViolationModalLogic {
  violationId: string | null;
  violation: ViolationDetail | null;
  isLoading: boolean;
  /**
   * Re-reads the case.
   *
   * The same cache entry the review dialog underneath is subscribed to, so one
   * refetch updates both — which is what makes "close this and read the
   * colleague's verdict" a single action rather than a reload.
   */
  reload: () => void;
  /**
   * The rule whose consequence is about to run.
   *
   * On the plain confirmation this is the row's own type. On the classify dialog
   * it is the one the moderator picked, and it is `null` until they do.
   */
  violationTypeId: string | null;
  /** Only the classify dialog uses this; the plain one never changes the rule. */
  setChosenTypeId: (value: string | null) => void;
  note: string;
  setNote: (value: string) => void;
  canSubmit: boolean;
  isSaving: boolean;
  submit: () => Promise<void>;
  error: string | null;
  isConflict: boolean;
  isGone: boolean;
}

export interface EnforceModalOptions {
  /**
   * Whether the moderator supplies the classification.
   *
   * `true` is the row the classifier could not judge at all: `violationTypeId`
   * is then **required** by the api, so the dialog cannot submit without one.
   * `false` is every other row, where sending a type would be ignored — so the
   * key is not sent at all rather than sent and discarded.
   */
  isClassifying: boolean;
}

/**
 * Confirming a violation, in the two shapes the api actually has.
 *
 * ## One hook, two dialogs, and the difference is a property of the ROW
 *
 * `POST /enforce` takes an optional `violationTypeId` that is required for
 * exactly the rows with no rule and ignored for all the others. A single dialog
 * carrying an optional field would be a 422 waiting for the day somebody opened
 * it on the wrong row; two registered dialogs choosing on
 * `violationTypeKey === null` make that unrepresentable, and this hook holds
 * what they genuinely share.
 *
 * ## There is no action picker, deliberately
 *
 * N20 says a consequence executes by reading the violation type's
 * configuration — so what a moderator confirms is the rule's **configured**
 * action, never one they choose here. A per-case override would be a second,
 * unconfigured door to suspending and banning. A moderator who wants a different
 * outcome dismisses the case and edits the rule.
 */
export function useEnforceViolationModalLogic(
  modalData: ModalData | undefined,
  onClose: () => void,
  { isClassifying }: EnforceModalOptions,
): UseEnforceViolationModalLogic {
  const { t } = useTranslation('violations');
  const toast = useToast();

  const [chosenTypeId, setChosenTypeId] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [refusal, setRefusal] = useState<ApiError | null>(null);

  const violationId = readViolationId(modalData);
  const query = useViolationQuery(violationId ?? '', { skip: violationId === null });
  const [enforceViolation, { isLoading: isSaving }] = useEnforceViolationMutation();

  const violation = query.data ?? null;
  const violationTypeId = isClassifying ? chosenTypeId : (violation?.violationTypeId ?? null);

  const submit = useCallback(async () => {
    if (violationId === null) return;
    if (isClassifying && chosenTypeId === null) return;

    setRefusal(null);
    const trimmed = note.trim();

    try {
      await enforceViolation({
        id: violationId,
        // `null`, never `''` — the schema trims and nullables it, so an empty
        // string would be a value on a field left blank on purpose.
        note: trimmed === '' ? null : trimmed,
        // The key is present ONLY when the moderator is supplying the
        // classification. `ViolationEnforceSchema` is `.strict()` but accepts the
        // key as optional, so sending `undefined` would serialize to an absent
        // key anyway — spreading is what makes that explicit rather than
        // incidental.
        ...(isClassifying && chosenTypeId !== null ? { violationTypeId: chosenTypeId } : {}),
      }).unwrap();

      toast.success(t('enforce.done'));
      onClose();
    } catch (caught) {
      setRefusal(toApiError(caught));
    }
  }, [chosenTypeId, enforceViolation, isClassifying, note, onClose, t, toast, violationId]);

  const copy = violationErrorCopy(refusal);

  return {
    violationId,
    violation,
    isLoading: query.isLoading,
    reload: () => {
      void query.refetch();
    },
    violationTypeId,
    setChosenTypeId,
    note,
    setNote,
    // A classify dialog with no rule chosen cannot submit: the api would answer
    // 422, and a disabled button is a better answer than a refusal.
    canSubmit: violation !== null && !isSaving && (!isClassifying || chosenTypeId !== null),
    isSaving,
    submit,
    error: refusal === null ? null : t(copy === null ? 'error.generic' : copy.i18nKey),
    isConflict: copy?.isConflict ?? false,
    isGone: copy?.isGone ?? false,
  };
}

/** Narrows the modal payload — `ModalData` is `Record<string, unknown>` by design. */
function readViolationId(data: ModalData | undefined): string | null {
  if (data === undefined) return null;

  const value = data['violationId'];

  return typeof value === 'string' && value !== '' ? value : null;
}
